import axios from 'axios';
import { QUEUE_TIMEOUT_CODE } from '../tools/downloadContracts';

// Result shaping (buildNoFormatsResult / buildConvertErrorResult) and the shared
// constants live in tools/downloadContracts.js — they do no network work, and ga.js
// needs them without importing this module. Import them from there directly: a
// re-export here would give the same value two import paths.

export const CONVERT_ENDPOINT = '/api/convert';
export const CONVERT_TIMEOUT = 45000;

// The server wraps the JSON body in these markers.
const JSON_MARKER = '#json#';

// Two pools, not one shared limit. The grid can fire a request per card, so it needs a
// cap; the main form must never queue behind those cards — with a 45s timeout a shared
// FIFO could stall the primary flow for the whole timeout.
export const REQUEST_POOL = {
    PRIMARY: 'primary',
    GRID: 'grid',
};

// Both pools target the same origin, so the two limits below must stay within what a
// browser opens in parallel per host — six connections on HTTP/1.1. Raise them only
// together with a check that the site is served over HTTP/2.
//
// The 2 + 4 = 6 budget is deliberately the whole per-host allowance, yet the page also
// makes same-origin calls that never pass through these pools (SearchResults.vue posts
// to /search/, api/instagram.js has its own endpoints). Those compete for the same six
// connections, so treat 6 as an upper bound already fully spent, not as headroom.
//
// The main form issues one request per submit; two slots only absorb a double click.
const PRIMARY_POOL_LIMIT = 2;
// A grid can hold dozens of cards, each able to fire its own request.
const GRID_POOL_LIMIT = 4;

// How long a queued request waits for a slot before giving up.
const QUEUE_WAIT_TIMEOUT = 15000;

const pools = {
    [REQUEST_POOL.PRIMARY]: { limit: PRIMARY_POOL_LIMIT, active: 0, waiting: [] },
    [REQUEST_POOL.GRID]: { limit: GRID_POOL_LIMIT, active: 0, waiting: [] },
};

function acquireSlot(pool, signal) {
    // Signing is resolved before we get here, so the request may already have been
    // aborted while it waited for it — and an 'abort' that fired earlier will never
    // reach the listener registered below. Without this check such a dead waiter sits
    // in the queue for the whole QUEUE_WAIT_TIMEOUT and fails as queue_timeout instead
    // of an immediate AbortError, holding a place the live cards need.
    if (signal?.aborted) {
        return Promise.reject(new DOMException('Aborted', 'AbortError'));
    }

    if (pool.active < pool.limit) {
        pool.active += 1;
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const entry = {};

        const drop = () => {
            const index = pool.waiting.indexOf(entry);
            if (index !== -1) {
                pool.waiting.splice(index, 1);
            }
        };

        // a waiter is cancelled too: otherwise it would take a slot after its card is
        // gone and hold it for the whole request
        const onAbort = () => {
            drop();
            clearTimeout(entry.timer);
            reject(new DOMException('Aborted', 'AbortError'));
        };

        // CONVERT_TIMEOUT only starts once a slot is taken, so without this the queue
        // itself could hold a card in its spinner for several timeouts in a row
        entry.timer = setTimeout(() => {
            drop();
            signal?.removeEventListener('abort', onAbort);
            // the code is what buildConvertErrorResult matches on: a bare Error reaches
            // ga.js unrecognised and sinks into the generic 'fatal' label
            const timeout = new Error('convert queue timeout');
            timeout.code = QUEUE_TIMEOUT_CODE;
            reject(timeout);
        }, QUEUE_WAIT_TIMEOUT);

        entry.take = () => {
            clearTimeout(entry.timer);
            signal?.removeEventListener('abort', onAbort);
            resolve();
        };

        pool.waiting.push(entry);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

function releaseSlot(pool) {
    const next = pool.waiting.shift();
    if (next) {
        // the slot is handed to the next waiter, so the counter stays unchanged
        next.take();
        return;
    }
    pool.active = Math.max(0, pool.active - 1);
}

/**
 * Unwrap the /api/convert response, which is wrapped in #json#...#json# markers.
 * @param data {*} - raw response body
 * @returns {*} parsed body, the original value when there is no wrapper, or null on malformed JSON
 */
export function parseConvertResponse(data) {
    if (typeof data === 'string' && data.startsWith(JSON_MARKER) && data.endsWith(JSON_MARKER)) {
        try {
            return JSON.parse(data.slice(JSON_MARKER.length, -JSON_MARKER.length));
        } catch (e) {
            return null;
        }
    }
    return data;
}

/**
 * Request available formats from /api/convert.
 * @param options {Object}
 * @param options.link {string} - video link
 * @param options.subscribeSignedRequestBody {Function} - Function that returns signed request body
 * @param options.signal {AbortSignal} - Abort signal
 * @param options.pool {string} - REQUEST_POOL.PRIMARY (default) or REQUEST_POOL.GRID
 * @returns {Promise<*>} parsed response body (null when the JSON is malformed)
 */
export async function requestConvert(options) {
    const { link, subscribeSignedRequestBody, signal, pool = REQUEST_POOL.PRIMARY } = options;
    const slot = pools[pool] || pools[REQUEST_POOL.PRIMARY];

    // Signing is resolved BEFORE taking a slot. Waiting for the signing chunk is not
    // network concurrency against our own backend, and holding a slot through it made
    // the pool serialise that wait: with a slow chunk every GRID slot sat idle waiting,
    // and the remaining cards got queue_timeout without the backend seeing a request.
    const body = await subscribeSignedRequestBody(link);

    await acquireSlot(slot, signal);

    try {
        // the card may have been destroyed while the request waited in the queue
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }

        const response = await axios.post(CONVERT_ENDPOINT, new URLSearchParams(body), {
            timeout: CONVERT_TIMEOUT,
            signal,
        });

        return parseConvertResponse(response.data);
    } finally {
        releaseSlot(slot);
    }
}

/**
 * Whether the error means the request was aborted, so callers do not have to import axios
 * just to recognise a cancellation.
 * @param e {Error}
 * @returns {boolean}
 */
export function isAbortError(e) {
    return axios.isCancel(e) || e.name === 'CanceledError' || e.name === 'AbortError';
}
